import { DeviceInteractionService } from './../../../../../device-interaction/device-interaction.service';
import { Component, ViewChild, OnInit, OnDestroy, ElementRef, AfterViewInit } from '@angular/core';
import { IAbstractWidgetComponent, ParameterChangedData, WidgetMode } from '../../shared/abstract-widget.component';
import { MessageDirective, MessageSeverity, MessageVariant } from 'src/app/directives/message.directive';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { DeviceOverviewWidgetSettingsFormType } from './device-overview-widget-settings-form-type';
import { interval, Subject, Subscription } from 'rxjs';
import { ClientDTO, SortDirection, ZoneDeviceDTO, ZoneDeviceState } from 'src/app/base/models/models.swagger.generated';
import { DialogModule } from '@syncfusion/ej2-angular-popups';
import { DialogComponent as SyncfusionDialogComponent } from '@syncfusion/ej2-angular-popups';
import { AuthService } from 'src/app/base/services/auth.service';
import { ClientAutocompleteComponent } from 'src/app/base/shared/client-autocomplete/client-autocomplete.component';

import { CustomMarkerOptions, MapBoxMapComponent, MapBoxMode, MapData } from 'src/app/base/shared/map-box-map/map-box-map.component';
import type { MarkerOptions, PopupOptions } from 'mapbox-gl';
import mapboxgl from 'mapbox-gl';

@Component({
    selector: 'app-device-overview-widget',
    imports: [TranslateModule, DialogModule, ReactiveFormsModule, MessageDirective, ClientAutocompleteComponent, MapBoxMapComponent],
    templateUrl: './device-overview-widget.component.html',
    styleUrl: './device-overview-widget.component.scss',
    providers: [DeviceInteractionService]
})
export class DeviceOverviewWidgetComponent implements OnInit, OnDestroy, AfterViewInit, IAbstractWidgetComponent {
  @ViewChild('mapContainer', { static: false })
  public mapContainer: ElementRef;

  public mapHeight: string;

  @ViewChild('widgetSettingsDialog')
  public widgetSettingsDialog: SyncfusionDialogComponent;

  // IAbstractWidgetComponent part
  public mode: WidgetMode = WidgetMode.View;
  public parametersChanged: Subject<ParameterChangedData> = new Subject<ParameterChangedData>();
  public defaultSizeX: number = 3;
  public defaultSizeY: number = 2;
  public isPersonalMode = false;

  public get title(): string {
    const title = this.editSettingsForm.controls.title.value;
    if (title && title.length > 0) {
      return title;
    }
    return "i18n.domain.home.dashboard.shared.widget-types.DeviceOverviewWidget";
  }

  public get selectedClientDto(): ClientDTO {
    return this.editSettingsForm.controls.selectedClient.value;
  }

  public set selectedClientDto(client: ClientDTO) {
    this.editSettingsForm.controls.selectedClient.setValue(client);
    this.deviceInteractionService.clientId = client?.id;
  }

  public get hasUserClientAssigned(): boolean {
    return this.authService.hasUserClientAssigned()
  }

  public mapData: MapData;

  public editSettingsForm: FormGroup<DeviceOverviewWidgetSettingsFormType>;

  public devices: ZoneDeviceDTO[] = [];
  public dataLoaded = false;

  protected WidgetMode = WidgetMode;
  protected MessageSeverity = MessageSeverity;
  protected MessageVariant = MessageVariant;

  private panelId: string;
  private onDeleteCallback: (panelId: string) => void;

  private autoRefreshSubscription: Subscription;

  constructor(private fb: FormBuilder,
              private authService: AuthService,
              private deviceInteractionService: DeviceInteractionService,
              private translateService: TranslateService) {

    this.editSettingsForm = this.fb.group<DeviceOverviewWidgetSettingsFormType>({
      title: this.fb.control<string>(null),
      selectedClient: this.fb.control<ClientDTO>(null),
    });

    if (this.hasUserClientAssigned) {
      this.selectedClientDto = this.authService.getUserInfo().client;
    }
  }

  ngOnInit() {
    this.getData();

    // Auto-refresh the grid every minute (60000 ms)
    this.autoRefreshSubscription = interval(60000).subscribe(() => {
      this.onRefresh();
    });
  }

  ngAfterViewInit(): void {
    this.updateMapHeight();
    window.addEventListener('resize', this.updateMapHeight.bind(this));
  }

  updateMapHeight(): void {
    if (this.mapContainer && this.mapContainer.nativeElement) {
      const height = this.mapContainer.nativeElement.offsetHeight;
      this.mapHeight = `${height}px`;
    }
  }

  public ngOnDestroy(): void {
    if (this.autoRefreshSubscription) {
      this.autoRefreshSubscription.unsubscribe();
    }
    window.removeEventListener('resize', this.updateMapHeight.bind(this));
  }

  public initializeParameters(panelId: string, serializedParameters: string, onDeleteCallback: (panelId: string) => void) {
    this.panelId = panelId;
    this.onDeleteCallback = onDeleteCallback;
    this.editSettingsForm.patchValue(JSON.parse(serializedParameters));

    const selectedClient = this.editSettingsForm.controls.selectedClient.value;
    if (selectedClient) {
      this.deviceInteractionService.clientId = selectedClient.id;
    }
  }

  public onDelete() {
    this.onDeleteCallback(this.panelId);
  }

  public onConfigure() {
    this.widgetSettingsDialog.show();
  }

  public onRefresh() {
    this.getData();
  }

  public onForceRefresh() {
    this.dataLoaded = false;
    this.devices = [];
    this.getData();
  }

  public onCloseSettingsDialog() {
    this.widgetSettingsDialog.hide();
  }

  public saveParameters() {
    this.widgetSettingsDialog.hide();
    this.parametersChanged.next({
      panelId: this.panelId,
      serializedParamerters: JSON.stringify(this.editSettingsForm.getRawValue())
    });
  }

  private getData(): void {
    this.deviceInteractionService.getData(null, null, null, "", SortDirection.Ascending).subscribe(res => {
      // this.devices = this.getTestDevices();
      this.devices = res.items;

      this.updateMapData();
      this.dataLoaded = true;
    });
  }

  private updateMapData(): void {
    const markerOptions: MarkerOptions[] = this.devices.map(device => this.createDeviceMarker(device));
    this.mapData = {
      data: this.getDeviceCoordinatesString(),
      mode: MapBoxMode.MultiPositionMode,
      markerOptions
    };
  }

  private getDeviceCoordinatesString(): string {
    return this.devices.map(device => `${device.coordinates}`).join('|');
  }

  private createDeviceMarker(device: ZoneDeviceDTO): CustomMarkerOptions {
    //Documentation:
    //https://docs.mapbox.com/mapbox-gl-js/guides/add-your-data/markers/

    const popupOptions: PopupOptions = {
      offset: 25,
      closeButton: true,
      closeOnClick: true,
      anchor: 'bottom',
      className: 'device-popup',
      maxWidth: '300px'
    };

    const stateLabel = this.translateService.instant('i18n.shared.zone-device-enums.state.' + ZoneDeviceState[device.state]);
    const stateColor = this.getDeviceStateColor(device.state);
    const popup = new mapboxgl.Popup(popupOptions).setHTML(
      `${device.name}<br>${device.zone?.name}<br>${this.translateService.instant('i18n.domain.device-interaction.grid.state')}: <span style="color: ${stateColor}; font-weight: bold;">${stateLabel}</span>`
    );

    return {
      color: this.getDeviceStateColor(device.state),
      popup: popup
    }
  }

  private getDeviceStateColor(state: ZoneDeviceState): string {
    switch (state) {
      case ZoneDeviceState.Present:
        return 'green';
      case ZoneDeviceState.Defect:
        return 'red';
      case ZoneDeviceState.NotPresent:
        return 'orange';
      case ZoneDeviceState.Unknown:
        return 'gray';
      default:
        return 'black';
    }
  }

  private getTestDevices(): ZoneDeviceDTO[] {
    if (this.dataLoaded){
      return [
        {
          id: 1,
          name: 'Device Zürich',
          coordinates: '47.38,8.54', // Zürich (moved slightly north)
          state: ZoneDeviceState.Present
        },
        {
          id: 2,
          name: 'Device Geneva',
          coordinates: '46.21,6.15', // Geneva (moved slightly west)
          state: ZoneDeviceState.Defect
        },
        {
          id: 3,
          name: 'Device Bern',
          coordinates: '46.95,7.45', // Bern (moved slightly east)
          state: ZoneDeviceState.NotPresent
        },
        {
          id: 4,
          name: 'Device Lucerne',
          coordinates: '47.06,8.32', // Lucerne (moved slightly north-east)
          state: ZoneDeviceState.Unknown
        }
      ] as ZoneDeviceDTO[];

    } else {
      return [
        {
          id: 1,
          name: 'Device Zürich',
          coordinates: '47.3769,8.5417', // Zürich
          state: ZoneDeviceState.NotPresent
        },
        {
          id: 2,
          name: 'Device Geneva',
          coordinates: '46.2044,6.1432', // Geneva
          state: ZoneDeviceState.NotPresent
        },
        {
          id: 3,
          name: 'Device Bern',
          coordinates: '46.9480,7.4474', // Bern
          state: ZoneDeviceState.Defect
        },
        {
          id: 4,
          name: 'Device Lucerne',
          coordinates: '47.0502,8.3093', // Lucerne
          state: ZoneDeviceState.Defect
        }
      ] as ZoneDeviceDTO[];
    }
  }
}
